import { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import { FieldArray, Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import ResendActivationLink from '../ResendActivationLink'
import { getAssetLink } from '../../../helpers/get-asset-link'
import ImageField from '../../FormLegacy/FormFieldsV2/ImageField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import GenderField from '../../FormLegacy/FormFieldsV2/GenderField'
import PhoneField from '../../FormLegacy/FormFieldsV2/PhoneField'
import { formValidation } from '../../FormLegacy/validation'
import {
  createTrainer,
  selectTrainerById,
  updateTrainer,
  selectActiveServices,
} from '../../../reducers/Organisation/organisationStaffReducer'
import ToggleField from '../../FormLegacy/FormFieldsV2/ToggleField'
import Filter from '../../Filter/Filter'
import FilterItem from '../../Filter/FilterItem'
import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import { selectTrainingTypesForFilter } from '../../../reducers/trainingsReducer'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

import TrainerClients from './TrainerClients'

const OrganisationStaffForm = ({
  trainer,
  params,
  createTrainer,
  updateTrainer,
  trainingTypes,
}) => {
  const { t } = useTranslation(['organisation', 'common'])
  const [formError, setFormError] = useState('')
  const { displaySnackbar } = useSnackbar()

  const formatTrainerServicesToValue = (services) => {
    if (!services) {
      return []
    }

    return services.map(
      ({ serviceId: { id, icon: avatar, color, name: text }, fee, percentage }) => {
        return {
          id,
          avatar,
          color,
          text,
          fee,
          percentage,
        }
      },
    )
  }

  const handleSubmitForm = async (
    {
      id,
      active,
      avatar,
      firstName,
      lastName,
      gender,
      phone,
      email,
      street,
      apartment,
      zipCode,
      services,
    },
    { setSubmitting },
  ) => {
    const isNew = isCreateNewMode()

    const newTrainerData = {
      firstName: firstName,
      lastName: lastName,
      gender: gender,
      phone: phone,
      active: active,
      street: street,
      apartment: apartment,
      zipCode: zipCode,
    }

    if (avatar.file) {
      newTrainerData.avatar = avatar.file
    }

    newTrainerData.services = JSON.stringify(
      services.map((service) => ({
        serviceId: service.text,
        fee: service.fee,
        percentage: service.percentage,
      })),
    )

    setFormError('')
    if (isNew) {
      delete newTrainerData.active
      newTrainerData.email = email
      try {
        setSubmitting(true)
        await createTrainer(newTrainerData)
        displaySnackbar(t('organisation:staff.form.toasts.created'))
        setSubmitting(false)
      } catch (err) {
        setSubmitting(false)
        if (err === 409) {
          setFormError(t('organisation:shared.errors.emailExists'))
        } else {
          setFormError(t('organisation:shared.serverError'))
        }
      }
    } else {
      try {
        setSubmitting(true)
        await updateTrainer(newTrainerData, id)
        setSubmitting(false)
        displaySnackbar(t('organisation:staff.form.toasts.updated'))
      } catch (err) {
        throw new Error(err)
      }
    }
  }

  const isCreateNewMode = () => params.trainerId && params.trainerId === 'new'

  const validationSchema = Yup.object().shape({
    firstName: formValidation.required,
    lastName: formValidation.required,
    email: formValidation.email,
    gender: formValidation.required,
    phone: formValidation.phone,
  })

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        id: trainer.id || '',
        avatar: {
          file: false,
          image: getAssetLink({ path: 'chitown-avatar/avatar', file: trainer.user.avatar }),
        },
        firstName: trainer.user.firstName || '',
        lastName: trainer.user.lastName || '',
        gender: trainer.gender || '',
        email: trainer.user.email || '',
        street: trainer.street || '',
        apartment: trainer.apartment || '',
        zipCode: trainer.zipCode || '',
        phone: trainer.phone || '',
        active: trainer.active === undefined ? false : trainer.active,
        services: formatTrainerServicesToValue(selectActiveServices(trainer.services)) || [],
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmitForm}
    >
      {({
        handleChange,
        handleSubmit,
        values,
        errors,
        touched,
        setFieldTouched,
        isSubmitting,
        dirty,
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <div
              className={`ctp-staff-trainer ${isCreateNewMode() ? 'ctp-staff-trainer--new' : ''}`}
            >
              <div className="ctp-staff-trainer__header">
                <h2>
                  {isCreateNewMode()
                    ? t('organisation:staff.form.titleNew')
                    : t('organisation:staff.form.titleEdit')}
                </h2>
              </div>
              <div className="columns">
                <div className="column is-5">
                  <div className="form form--object-profile">
                    <div>
                      <ImageField
                        name={'avatar'}
                        label={t('organisation:shared.fields.avatar.label')}
                        placeholderImage={getAssetLink({
                          path: 'chitown-avatar/avatar',
                          file: 'avatar.png',
                        })}
                        imageRatio={1}
                        value={values.avatar}
                        onChange={handleChange}
                      />
                      <TextField
                        name={'firstName'}
                        label={t('organisation:shared.fields.firstName.label')}
                        placeholder={t('organisation:shared.fields.firstName.placeholder')}
                        value={values.firstName}
                        onChange={handleChange}
                        isError={touched.firstName && !!errors.firstName}
                        onBlur={() => setFieldTouched('firstName')}
                      />
                      <TextField
                        name={'lastName'}
                        label={t('organisation:shared.fields.lastName.label')}
                        placeholder={t('organisation:shared.fields.lastName.placeholder')}
                        value={values.lastName}
                        onChange={handleChange}
                        isError={touched.lastName && !!errors.lastName}
                        onBlur={() => setFieldTouched('lastName')}
                      />
                      <TextField
                        name={'email'}
                        label={t('organisation:shared.fields.email.label')}
                        placeholder={t('organisation:shared.fields.email.placeholder')}
                        value={values.email}
                        onChange={handleChange}
                        isError={touched.email && !!errors.email}
                        errorLabel={touched.email && errors.email}
                        onBlur={() => setFieldTouched('email')}
                        disabled={!isCreateNewMode()}
                      />
                      <div className="columns">
                        <div className="column">
                          <GenderField
                            name={'gender'}
                            label={t('organisation:staff.form.gender.label')}
                            value={values.gender}
                            onChange={handleChange}
                            isError={touched.gender && !!errors.gender}
                          />
                        </div>
                        <div className="column">
                          <PhoneField
                            type={'number'}
                            name={'phone'}
                            label={t('organisation:staff.form.phone.label')}
                            placeholder={t('organisation:staff.form.phone.placeholder')}
                            value={values.phone}
                            onChange={handleChange}
                            isError={touched.phone && !!errors.phone}
                            errorLabel={touched.phone && errors.phone}
                            onBlur={() => setFieldTouched('phone')}
                          />
                        </div>
                      </div>
                      <div className="field field-address">
                        <div className="columns">
                          <div className={'column is-6'}>
                            <TextField
                              name={'street'}
                              label={t('organisation:staff.form.address.label')}
                              placeholder={''}
                              value={values.street}
                              onChange={handleChange}
                              isError={touched.street && !!errors.street}
                              onBlur={() => setFieldTouched('street')}
                            />
                          </div>
                          <div className={'column'}>
                            <TextField
                              name={'apartment'}
                              label={t('organisation:staff.form.apartment.label')}
                              placeholder={''}
                              value={values.apartment}
                              onChange={handleChange}
                              isError={touched.apartment && !!errors.apartment}
                              onBlur={() => setFieldTouched('apartment')}
                            />
                          </div>
                          <div className={'column is-3'}>
                            <TextField
                              name={'zipCode'}
                              label={t('organisation:staff.form.zipCode.label')}
                              placeholder={''}
                              value={values.zipCode}
                              onChange={handleChange}
                              isError={touched.zipCode && !!errors.zipCode}
                              onBlur={() => setFieldTouched('zipCode')}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {trainer.clients && trainer.clients.length > 0 && (
                    <TrainerClients trainerData={trainer} />
                  )}
                </div>
                <div className="column is-7">
                  {!isCreateNewMode() && (
                    <div className="ctp-staff-trainer__toggle">
                      <ToggleField
                        name={'active'}
                        label={t('organisation:staff.form.active')}
                        value={values.active}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                  {!isCreateNewMode() && values.email && (
                    <ResendActivationLink
                      email={values.email}
                      onError={() => {
                        setFormError(t('organisation:shared.serverError'))
                      }}
                    />
                  )}
                  <div className="ctp-staff-trainer__services">
                    <FieldArray name={'services'}>
                      {(arrayHelpers) => {
                        return (
                          <Filter
                            name={'services'}
                            placeholder={t('organisation:staff.form.servicesPlaceholder')}
                            options={trainingTypes}
                            value={values.services}
                            config={{ multiple: true, displayValuesList: true }}
                            onChange={handleChange}
                            renderListItem={({ item, index, ...props }) => {
                              return (
                                <div key={item.id} className={'columns'}>
                                  <div className="column is-7">
                                    <FilterItem item={item} {...props} />
                                  </div>
                                  <div className="column is-3">
                                    <SelectField
                                      name={`services.${index}.percentage`}
                                      options={[
                                        {
                                          value: 'percentage',
                                          label: t(
                                            'organisation:staff.form.serviceType.percentage',
                                          ),
                                        },
                                        {
                                          value: 'rates',
                                          label: t('organisation:staff.form.serviceType.rates'),
                                        },
                                      ]}
                                      placeholder={t(
                                        'organisation:staff.form.serviceType.selectPlaceholder',
                                      )}
                                      value={item.percentage ? 'percentage' : 'rates'}
                                      onChange={(e) => {
                                        arrayHelpers.replace(index, {
                                          ...item,
                                          percentage: Number(e.target.value === 'percentage'),
                                        })
                                      }}
                                    />
                                  </div>
                                  <div className="column is-2">
                                    <TextField
                                      type={'number'}
                                      name={`services.${index}.fee`}
                                      placeholder={'0'}
                                      value={item.fee}
                                      onChange={handleChange}
                                    />
                                  </div>
                                </div>
                              )
                            }}
                          />
                        )
                      }}
                    </FieldArray>
                  </div>
                </div>
              </div>
              <div className="columns">
                <div className="column">
                  <div className="ctp-staff-trainer__actions">
                    <button
                      type={'submit'}
                      className={`button button-primary ${isSubmitting ? 'is-loading' : ''}`}
                      disabled={!dirty || isSubmitting}
                    >
                      {t('common:actions.save')}
                    </button>
                    <label className="help is-danger">{formError}</label>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

OrganisationStaffForm.propTypes = {
  params: PropTypes.object,
  trainer: PropTypes.object.isRequired,
  createTrainer: PropTypes.func.isRequired,
  updateTrainer: PropTypes.func.isRequired,
  trainingTypes: PropTypes.array.isRequired,
}

export default connect(
  (state, { params }) => ({
    trainer: selectTrainerById(state, params.trainerId),
    trainingTypes: selectTrainingTypesForFilter(state),
  }),
  { createTrainer, updateTrainer },
)(OrganisationStaffForm)
